import { getClient } from "../client";
import {
  DM_TYPE,
  MESSAGE_PAGE_SIZE,
  ROOM_TYPE,
  TEMPORARY_MESSAGE_PREFIX,
} from "../constants";
import { flattenRawChannel, upsertChannel } from "../entities/channels";
import { appendChannelMessage, flattenRawMessage } from "../entities/messages";
import { flattenRawUser, updateUser, updateUsers } from "../entities/users";
import { rawChannelsById } from "../state";

function getChannel(channelId) {
  const entry = rawChannelsById.get(channelId);
  if (!entry) {
    throw new Error(`Channel ${channelId} not found in cache`);
  }
  return entry.rawChannel;
}

function validateRoomType(channelType) {
  if (channelType !== ROOM_TYPE) {
    throw new Error(`Cannot perform action on ${channelType}`);
  }
}

function validateDMType(channelType) {
  if (channelType !== DM_TYPE) {
    throw new Error(`Cannot perform action on ${channelType}`);
  }
}

export async function sendKeystroke(channelId) {
  const channel = getChannel(channelId);
  return channel.keystroke();
}

export async function loadOlderMessages(channelId, oldestMessageId) {
  const channel = getChannel(channelId);
  const { messages: newMessages } = await channel.query({
    messages: { limit: MESSAGE_PAGE_SIZE, id_lt: oldestMessageId },
  });
  return newMessages;
}

export async function loadMoreChannelMembers(channelId, offset) {
  const { rawChannel } = rawChannelsById.get(channelId);
  const options = { limit: 100 };
  if (offset && offset > 0) {
    options.offset = offset;
  }
  const results = await rawChannel.queryMembers({}, {}, options);
  // store each user in the usersById react state
  const flattenedUsers = results.members.map((member) =>
    flattenRawUser(member.user)
  );
  updateUsers(flattenedUsers);
  // update the channel state with a reference to each user
  results.members.forEach((rawUser) => {
    rawChannel.state.members[rawUser.user_id] = rawUser;
  });
  // update react channel with new membership list
  upsertChannel(flattenRawChannel(rawChannel));
}

export async function sendMarkRead(channelId) {
  const channel = getChannel(channelId);
  return channel.markRead();
}

export async function sendAddReaction(channelId, messageId, reactionId) {
  const channel = getChannel(channelId);
  return channel.sendReaction(messageId, { type: reactionId });
}

export async function sendRemoveReaction(channelId, messageId, reactionId) {
  const channel = getChannel(channelId);
  return channel.deleteReaction(messageId, reactionId);
}

export async function sendRemoveMembers(channelId, userId) {
  const channel = getChannel(channelId);
  validateRoomType(channel.type); // TODO: ensure this is enforced via GetStream's auth rules
  return channel.removeMembers([userId]);
}

export async function sendHideChannel(channelId) {
  const channel = getChannel(channelId);
  validateDMType(channel.type); // TODO: ensure this is enforced via GetStream's auth rules
  return channel.hide();
}

export async function sendMuteChannel(channelId, muteOptions) {
  const channel = getChannel(channelId);
  return channel.mute(muteOptions);
}

export async function sendUnmuteChannel(channelId) {
  const channel = getChannel(channelId);
  return channel.unmute();
}

export function registerOptimisticMessage(channelId, userId, markdown) {
  const newMessage = flattenRawMessage({
    // starts with a zeroed out UUID, the last part of the UUID is a random string of 12 numbers to give some uniqueness
    id: `${TEMPORARY_MESSAGE_PREFIX}${Math.random().toString().substr(2, 12)}`,
    user: {
      id: userId,
    },
    text: markdown,
    created_at: new Date(),
  });
  appendChannelMessage(channelId, newMessage);
  return newMessage;
}

export async function loadUser(userId) {
  const { users } = await getClient().queryUsers({
    id: userId,
  });
  if (users.length) {
    updateUser(flattenRawUser(users[0]));
  }
}
